import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useActiveChatStore } from '#modules/chat/model/activeChatStore';
import { useChatSettingsStore } from '#modules/chat/model/chatSettingsStore';
import { useChatTypingStore } from '#modules/chat/model/chatTypingStore';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { getCachedNativeToken } from '#features/api/utils/nativeAuth';
import { applyWsCallEvent } from '#features/calls/utils/applyWsCallEvent';
import { playNotificationSound } from '#features/chats/utils/playNotificationSound';
import { i18n } from '#features/i18n/i18n';
import { getPathnamePrefix } from '#features/i18n/utils/getPathnamePrefix';
import { markNetworkProven } from '#features/network/model/connectivity';
import { catchUp } from '#features/network/utils/catchUp';
import { watchNativeAppState } from '#features/network/utils/watchNativeAppState';
import { useBellAnimationStore } from '#features/notifications/model/bellAnimationStore';
import { updateAppBadge } from '#features/notifications/utils/appBadge';
import {
  claimIosNotification,
  type IosNotificationClaim,
} from '#features/notifications/utils/claimIosNotification';
import {
  parseForegroundNotification,
  watchNativeForegroundNotifications,
  type ForegroundNotification,
} from '#features/notifications/utils/nativeForegroundNotifications';
import {
  invalidateFor,
  isSessionRoomChat,
  LOCAL_EVENTS,
} from '#features/realtime/cacheInvalidation';
import {
  shouldSuppressChatToast,
  shouldSuppressToastByGame,
} from '#features/realtime/toastFilters';
import { applyWsChatTypingEvent } from '#features/realtime/utils/applyWsChatTypingEvent';
import {
  parseWsMessage,
  type WsMessage,
  type WsOutboundData,
} from '#features/realtime/wsMessageParser';
import { toaster } from '#features/ui/components/Toaster';
import { envConfig } from '#constants/envConfig';

// Backend enrichment substitutes this placeholder when the actor has no uploaded
// avatar. Treat it as "no avatar" so the toast falls back to the coloured initial
// (consistent with avatars elsewhere) instead of rendering a blank image.
const DEFAULT_AVATAR_FILENAME = 'default-avatar.png';

const toAvatarUrl = (path: string | null | undefined): string | undefined => {
  if (!path) return undefined;
  if (path.endsWith(DEFAULT_AVATAR_FILENAME)) return undefined;
  if (path.startsWith('http')) return path;
  return `${envConfig.MEDIA_URL}/mh/user/${path}`;
};

const WS_PATH = '/ws';
const PING_INTERVAL_MS = 25_000;
const BASE_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const WS_CLOSE_CODE_RECONNECT = 1000;
const WS_CLOSE_CODE_PING_FAILED = 3001;
const WS_CLOSE_CODE_SOCKET_ERROR = 3002;

const toWsProtocol = (protocol: string): 'ws:' | 'wss:' => (protocol === 'https:' ? 'wss:' : 'ws:');

const toWsUrlFromHttp = (urlValue: string): string | null => {
  if (!envConfig.IN_BROWSER) {
    return null;
  }

  try {
    const parsed = new URL(urlValue, window.location.origin);
    parsed.protocol = toWsProtocol(parsed.protocol);
    parsed.pathname = WS_PATH;
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch {
    return null;
  }
};

const normalizeWsUrl = (input?: string): string | null => {
  if (!envConfig.IN_BROWSER) {
    return null;
  }

  const value = input?.trim();
  if (!value) {
    return null;
  }

  if (value.startsWith('ws://') || value.startsWith('wss://')) {
    return value;
  }

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return toWsUrlFromHttp(value);
  }

  if (value.startsWith('/')) {
    return `${toWsProtocol(window.location.protocol)}//${window.location.host}${value}`;
  }

  return null;
};

// The native shell can't set a Cookie header on the WS handshake (and has no
// shared cookie jar anyway), so it passes the JWT as a `?token=` query param,
// which teamplay-ws reads as a fallback after the cookie. Web never hits this
// (IS_NATIVE folds to false; the web client also strips the query server-side).
const withNativeToken = (wsUrl: string | null): string | null => {
  if (!wsUrl || !envConfig.IS_NATIVE) {
    return wsUrl;
  }
  const token = getCachedNativeToken();
  if (!token) {
    return wsUrl;
  }
  const separator = wsUrl.includes('?') ? '&' : '?';
  return `${wsUrl}${separator}token=${encodeURIComponent(token)}`;
};

const resolveWsUrl = (): string | null => {
  if (!envConfig.IN_BROWSER) {
    return null;
  }

  const explicit = normalizeWsUrl(envConfig.WS_URL);
  if (explicit) {
    return withNativeToken(explicit);
  }

  const byApiUrl = envConfig.API_URL ? toWsUrlFromHttp(envConfig.API_URL) : null;
  if (byApiUrl) {
    return withNativeToken(byApiUrl);
  }

  return withNativeToken(
    `${toWsProtocol(window.location.protocol)}//${window.location.host}${WS_PATH}`
  );
};

let wsInstance: WebSocket | null = null;

/** Разброс перечитывания после переподключения сокета, мс. */
const RECONNECT_REFRESH_JITTER_MS = 3_000;

const reconnectCallbacks = new Set<() => void>();

export function onWsReconnect(callback: () => void): () => void {
  reconnectCallbacks.add(callback);
  return () => {
    reconnectCallbacks.delete(callback);
  };
}

export function sendWsMessage(message: object): void {
  if (wsInstance?.readyState === WebSocket.OPEN) {
    wsInstance.send(JSON.stringify(message));
  }
}

export const useRealtimeNotifications = () => {
  const isAuth = useIsAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);

  const wsUrl = resolveWsUrl();

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const clearPingTimer = () => {
      if (pingTimerRef.current !== null) {
        window.clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    };

    const closeSocket = () => {
      const socket = socketRef.current;
      socketRef.current = null;
      wsInstance = null;
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
      ) {
        socket.close(WS_CLOSE_CODE_RECONNECT, 'reconnect');
      }
    };

    if (!envConfig.IN_BROWSER || !isAuth || !wsUrl) {
      useChatTypingStore.getState().clearAllTyping();
      clearReconnectTimer();
      clearPingTimer();
      closeSocket();
      return;
    }

    const activeWsUrl = wsUrl;
    let isDisposed = false;
    let appActive = !envConfig.IS_NATIVE;
    const isForeground = () => !isDisposed && appActive && document.visibilityState !== 'hidden';
    const pendingPresentations = new Set<() => void>();
    const stopAppState = watchNativeAppState((active) => {
      appActive = active;
    });
    /** Сокет уже открывался в этом сеансе: следующее открытие - переподключение. */
    let openedBefore = false;

    /**
     * Кадр сокета → ключи кеша.
     *
     * Само правило – в карте инвалидации (`#features/realtime/cacheInvalidation`): какое
     * событие какие ключи роняет и почему, написано там одним списком, вместе с собственными
     * действиями человека. Здесь остаётся только то, чего в кадре нет: кто я, своё ли это
     * сообщение и относится ли переписка к сессии, которую держит комната.
     */
    const invalidateByType = (
      type: string,
      data: WsOutboundData | null,
      actorUserId?: string | null
    ) => {
      const parentId = data?.parentId ?? null;

      void invalidateFor(queryClient, type, {
        parentId,
        targetId: data?.targetId ?? null,
        isOwnMessage: Boolean(actorUserId) && actorUserId === getCurrentUserId(),
        // Кадры о прочтении и удалении приходят одним типом и для личного диалога, и для
        // переписки сессии: различает их только сам чат.
        isRoomChat: isSessionRoomChat(queryClient, parentId),
      });
    };

    const buildLocalePath = (path: string): string => {
      const prefix = getPathnamePrefix(i18n.locale);
      return prefix ? `/${prefix}${path}` : path;
    };

    const playWsNotificationSound = () => {
      const { soundEnabled } = useChatSettingsStore.getState();
      if (soundEnabled) {
        playNotificationSound();
      }
    };

    const getCurrentUserId = (): string | null => {
      const meData = queryClient.getQueryData<{ __typename?: string; id?: string } | null>(['me']);
      return meData && meData.__typename === 'CurrentUser' ? (meData.id ?? null) : null;
    };

    const getCurrentUserGameIds = (): string[] | null => {
      const meData = queryClient.getQueryData<{
        __typename?: string;
        gameIds?: (string | null)[] | null;
      } | null>(['me']);
      if (!meData || meData.__typename !== 'CurrentUser') return null;
      return (meData.gameIds ?? []).filter((g): g is string => typeof g === 'string');
    };

    // Signal events ride on the same Plan 2 pipeline and carry a messageKey for
    // schema reasons (ws_template.message_key_code is NOT NULL), but they must
    // not produce a toast — they update typing dots / read ticks / reaction
    // counters via invalidation + side effects instead.
    const SIGNAL_EVENT_TYPES = new Set<string>([
      'CHAT_TYPING',
      'CHAT_READ',
      'CHAT_REACTION',
      'CHAT_MESSAGES_READ', // legacy aliases
      'CHAT_MESSAGE_REACTION',
      // Собеседник убрал своё сообщение. Кадр нужен, чтобы на месте текста встало надгробие;
      // сказать об этом словами — значит показать человеку, что от него что-то скрыли.
      'CHAT_MESSAGE_DELETED',
      // I left a chat myself, on another device. The frame exists so this tab drops the row
      // from the list; telling me about my own tap would be noise.
      'CHAT_LEFT',
    ]);

    // POST_UPDATED / POST_DELETED fan out to every connected user on every post
    // change and aren't interesting as a popup — keep them muted (cache
    // invalidation in the switch above still keeps Discover fresh). POST_CREATED
    // is NOT muted: it's a "new LFG post" announcement, surfaced as a toast but
    // game-filtered (see shouldSuppressToastByGame) just like SESSION_CREATED.
    const TOAST_MUTED_BROADCAST_EVENT_TYPES = new Set<string>(['POST_UPDATED', 'POST_DELETED']);

    // Comment events reach two audiences: the post author (per-user delivery, carries
    // hasNotification=true from the sibling BELL rule) and every viewer of the post
    // (POST_COMMENTS_TOPIC broadcast, hasNotification=false — for live inline updates
    // only). Toast just the former; viewers get the comment inline, not a popup.
    const COMMENT_EVENT_TYPES = new Set<string>(['COMMENT_CREATED', 'COMMENT_REPLY']);

    const showNotificationToast = async (message: WsMessage, fallback?: ForegroundNotification) => {
      if (!isForeground()) return;
      const { messageKey, params, parentId, targetId } = message.data ?? {};
      if (!messageKey && !fallback) return;
      if (SIGNAL_EVENT_TYPES.has(message.type)) return;
      if (TOAST_MUTED_BROADCAST_EVENT_TYPES.has(message.type)) return;
      if (COMMENT_EVENT_TYPES.has(message.type) && !message.data?.hasNotification) return;

      const currentUserId = getCurrentUserId();
      const originSocket = socketRef.current;
      const acknowledgePresentation = () => {
        if (
          !fallback &&
          isForeground() &&
          getCurrentUserId() === currentUserId &&
          message.data?.notificationId
        ) {
          // Send on this effect's connection: reconnect/account changes must not acknowledge
          // a frame on a different authenticated connection.
          const socket = socketRef.current;
          if (socket === originSocket && socket?.readyState === WebSocket.OPEN) {
            try {
              socket.send(
                JSON.stringify({ action: 'presented', notificationId: message.data.notificationId })
              );
            } catch {
              /* Missing receipt falls back to push. */
            }
          }
        }
      };
      const consumeWithoutAlert = async () => {
        // Reading/muting the conversation consumes a foreground push too: otherwise
        // the native watchdog would mistake intentional silence for an unavailable JS UI.
        if (message.data?.notificationId) {
          const reservation = await claimIosNotification(message.type, message.data.notificationId);
          if (!isForeground() || getCurrentUserId() !== currentUserId) {
            reservation.finish?.(false);
            return;
          }
          reservation.finish?.(true);
          acknowledgePresentation();
        }
      };

      // Don't show toast about own actions (e.g. "you joined session" when you clicked Join)
      // Scheduled reminders and matching alerts intentionally notify their own author.
      const isSelfReminder = [
        'POST_BUMP_REMINDER',
        'POST_MATCH_FOUND',
        'POST_MATCH_ALERTS_EXPIRING',
        'POST_MATCH_ALERTS_NOTHING_FOUND',
        'POST_RESTORED',
      ].includes(message.type);
      if (!isSelfReminder && currentUserId && message.actor?.userId === currentUserId) {
        await consumeWithoutAlert();
        return;
      }

      // Broadcast session announcements (SESSION_CREATED) reach every online
      // user; only toast players who have this game in their profile.
      if (shouldSuppressToastByGame(message.type, message.data?.gameId, getCurrentUserGameIds())) {
        await consumeWithoutAlert();
        return;
      }

      // Chat: don't toast/sound for the conversation the user is already viewing,
      // when the tab is backgrounded, or when the user muted this chat — the
      // unread badge still updates.
      const tabHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const chatMuted = useChatSettingsStore.getState().isChatMuted(parentId);
      // Открытых лент бывает несколько: окно комнаты сессии рисует переписку поверх любой
      // страницы, поэтому спрашиваем стор про эту переписку, а не сравниваем с единственной.
      if (
        shouldSuppressChatToast({
          type: message.type,
          chatOpen: useActiveChatStore.getState().isChatOpen(parentId),
          tabHidden,
          muted: chatMuted,
        })
      ) {
        await consumeWithoutAlert();
        return;
      }

      const text = fallback?.text ?? i18n._(messageKey!, params ?? {});

      const isInvite = message.type === 'SESSION_MEMBER_INVITED';
      // «Команда собралась в голосе» ценно ровно сейчас: даём восемь секунд вместо пяти, чтобы
      // человек успел прочитать и нажать, но не превращаем в баннер — разговор идёт без него.
      const isVoiceStarted = message.type === 'SESSION_VOICE_STARTED';
      const isMissedCall = message.type === 'CALL_MISSED';

      let href: string | undefined;
      // Переписка сессии – такой же тред, как личная: `parentId` у обоих типов равен chatId.
      // Общая ветка ниже увела бы её на `/posts/{chatId}`, то есть на несуществующую заявку.
      if (
        (message.type === 'CHAT_MESSAGE' ||
          message.type === 'SESSION_CHAT_MESSAGE' ||
          message.type === 'CALL_MISSED') &&
        parentId
      ) {
        href = buildLocalePath(`/dashboard/chats/${parentId}`);
      } else if (
        ['SESSION_CANCEL', 'SESSION_COMPLETED', 'SESSION_MEMBER_REMOVED'].includes(message.type) &&
        parentId &&
        targetId
      ) {
        href = buildLocalePath(
          `/posts/${parentId}?session=${encodeURIComponent(targetId)}&sessionNotice=1`
        );
      } else if (isVoiceStarted && parentId && targetId) {
        href = buildLocalePath(`/posts/${parentId}?session=${encodeURIComponent(targetId)}`);
      } else if (message.type === 'POST_DELETED') {
        // The post no longer exists — a link to /posts/{id} would 404.
        href = undefined;
      } else if (message.type === 'CHAT_DELETED') {
        // The chat is gone; deep-linking to /dashboard/chats/{id} would 404.
        // Instead the toast points at the actor's profile (the peer who deleted),
        // so the recipient can quickly open their page if they want to follow up.
        const actorId = message.actor?.userId;
        href = actorId ? buildLocalePath(`/players/${actorId}`) : undefined;
      } else if (parentId) {
        href = buildLocalePath(`/posts/${parentId}`);
      }

      // Native WS and foreground push fallback share one event reservation.
      // Cache refresh and persistent notification history remain independent of presentation.
      let claim: IosNotificationClaim | undefined;
      if (message.data?.notificationId) {
        claim = await claimIosNotification(message.type, message.data.notificationId);
        if (!claim.claimed) return;
        // Account changes can commit while the native reservation promise is outstanding.
        if (
          getCurrentUserId() !== currentUserId ||
          (fallback?.recipientId && fallback.recipientId !== getCurrentUserId())
        ) {
          claim.finish?.(true);
          return;
        }
        if (!isForeground() || (claim.expiresAt !== undefined && Date.now() >= claim.expiresAt)) {
          claim.finish?.(false);
          return;
        }
      }

      let settled = false;
      let wasShown = false;
      let timer: number | undefined;
      let toastId: string | undefined;
      const finish = (shown: boolean) => {
        if (settled) return;
        settled = true;
        wasShown = shown;
        if (timer !== undefined) window.clearTimeout(timer);
        pendingPresentations.delete(cancel);
        claim?.finish?.(shown);
        if (shown) {
          acknowledgePresentation();
          playWsNotificationSound();
        }
      };
      const cancel = () => {
        finish(false);
        if (toastId) toaster.remove(toastId);
      };
      try {
        toastId = toaster.create({
          onStatusChange: ({ status }) => {
            if (status === 'visible') {
              if (
                !isForeground() ||
                getCurrentUserId() !== currentUserId ||
                (!fallback && socketRef.current !== originSocket) ||
                (claim?.expiresAt !== undefined && Date.now() >= claim.expiresAt)
              ) {
                cancel();
                return;
              }
              finish(true);
            } else if (status === 'unmounted') finish(false);
          },
          type: 'info',
          duration: isInvite ? 10_000 : isVoiceStarted || isMissedCall ? 8_000 : 5_000,
          title: text,
          meta: {
            ...(fallback?.href || href ? { href: fallback?.href ?? href } : undefined),
            avatar: toAvatarUrl(message.actor?.avatar),
            avatarName: message.actor?.displayName,
            ...(isInvite && targetId
              ? { sessionInvite: { sessionId: targetId, postId: parentId ?? undefined } }
              : undefined),
          },
        });
        if (!settled) {
          pendingPresentations.add(cancel);
          // A queued toast must not consume the native lease or acknowledge a server delivery.
          // Remove it before the push fallback can produce a second, delayed toast.
          if (message.data?.notificationId) {
            timer = window.setTimeout(
              cancel,
              Math.max(0, (claim?.expiresAt ?? Date.now() + 1500) - Date.now())
            );
          }
        } else if (!wasShown && toastId) toaster.remove(toastId);
      } catch (error) {
        cancel();
        throw error;
      }
    };

    /**
     * Снять с экрана уведомления этого разговора.
     *
     * <p>Работает только пока жива хотя бы одна вкладка: закрытой снимать нечем, и там
     * уведомление доживёт до клика — как у любого веб-приложения.
     */
    const dismissNotifications = async (tag?: string | null) => {
      if (!tag || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return;
      }
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        const shown = (await registration?.getNotifications({ tag })) ?? [];
        shown.forEach((notification) => notification.close());
        if (shown.length > 0) {
          await updateAppBadge(registration);
        }
      } catch {
        // Уведомление просто останется висеть до клика — не повод ронять обработку кадра.
      }
    };

    const handleSideEffects = (message: WsMessage) => {
      // 1:1 calls: frames carry messageKey=null (never toasted by the generic path) and drive
      // the global CallOverlay via the call store. currentUserId disambiguates multi-tab cases
      // (answered/declined on another device).
      //
      // Routed by prefix rather than by an explicit case list, because a frame nobody
      // dispatches is invisible: CALL_MOVED shipped with a handler in applyWsCallEvent and no
      // case here, so a call moved to the desktop left the phone in the LiveKit room —
      // publishing its microphone — until someone hung up (prod, call 1ecc5449, 18.08.2026).
      // The cases below only add the effects that are specific to one outcome.
      if (message.type.startsWith('CALL_')) {
        applyWsCallEvent(message, getCurrentUserId());
      }

      switch (message.type) {
        // «Убери уведомление, его уже прочитали» — приходит, когда человек открыл разговор на
        // другом устройстве. По вебсокету, а не пушем: Chrome требует, чтобы каждое доставленное
        // push-сообщение что-нибудь показало, и закрытие уже висящего за показ не считает —
        // вместо исчезновения человек увидел бы «сайт обновлён в фоновом режиме».
        case 'PUSH_DISMISS': {
          void dismissNotifications(message.data?.params?.tag ?? message.data?.parentId);
          break;
        }
        case 'CALL_MISSED': {
          // The callee gets a regular top-right notification toast (same style as
          // the rest of the WS feed) with a deep link into the chat to call back.
          const missedCurrentUserId = getCurrentUserId();
          const callId = message.data?.params?.callId;
          if (missedCurrentUserId && message.actor?.userId !== missedCurrentUserId) {
            void showNotificationToast({
              ...message,
              data: {
                notificationId: callId ? `call-missed:${callId}:${missedCurrentUserId}` : null,
                messageKey: 'notification.call_missed',
                params: { user: message.actor?.displayName || 'Player' },
                parentId: message.data?.parentId ?? null,
                targetId: callId ?? null,
                gameId: null,
                hasNotification: false,
              },
            });
          }
          // Системная карточка звонка в ленте и превью в списке переписок – ключи этих кадров
          // (как и кадров о подписке) стоят в карте инвалидации рядом со всеми остальными.
          break;
        }
        case 'SESSION_CHAT_MESSAGE':
        case 'CHAT_MESSAGE':
          // Сообщение пришло — значит собеседник допечатал. Само по себе это очевидно, но
          // «печатает» так не гаснет: событие остановки приходит тем же типом CHAT_TYPING и
          // без признака, поэтому получатель продлевает подсказку вместо того, чтобы снять её,
          // и она висит ещё три секунды поверх только что доставленного сообщения.
          if (message.data?.parentId) {
            useChatTypingStore.getState().clearTyping(message.data.parentId);
          }
          break;
        case 'CHAT_TYPING':
          if (message.data?.parentId && message.actor?.userId) {
            applyWsChatTypingEvent(
              message.data.parentId,
              message.actor.userId,
              message.data.params?.typing
            );
          }
          break;
        case 'CHAT_DELETED':
          if (message.data?.parentId) {
            useChatTypingStore.getState().clearTyping(message.data.parentId);
            // Комната сессии переживает уход с маршрута, и её ленту снимать некому: пометка в
            // сторе – единственный способ показать «Чат удалён» вместо мёртвой переписки.
            useActiveChatStore.getState().markChatDeleted(message.data.parentId);
            // If the user is currently viewing this chat, navigate away to
            // /dashboard/chats — the detail route would 404 on next refetch.
            if (typeof window !== 'undefined') {
              const path = window.location.pathname;
              const deletedChatId = message.data.parentId;
              if (path.includes(`/dashboard/chats/${deletedChatId}`)) {
                const localeMatch = path.match(/^\/([a-z]{2})\//);
                const prefix = localeMatch ? `/${localeMatch[1]}` : '';
                window.location.assign(`${prefix}/dashboard/chats`);
              }
            }
          }
          break;
      }
    };

    const handleIncomingMessage = (raw: string) => {
      const message = parseWsMessage(raw);
      if (!message) return;

      // 1. Query invalidation by type
      invalidateByType(message.type, message.data, message.actor?.userId);

      // 2. Toast (if has messageKey)
      if (message.data?.messageKey) {
        void showNotificationToast(message);
      }

      // 3. Bell animation (if persistent notification created)
      if (message.data?.hasNotification) {
        useBellAnimationStore.getState().triggerWiggle();
        void invalidateFor(queryClient, LOCAL_EVENTS.notificationCreated);
      }

      // 4. Type-specific side effects
      handleSideEffects(message);
    };

    const stopForegroundNotifications = watchNativeForegroundNotifications((payload) => {
      const notification = parseForegroundNotification(payload);
      if (!notification) return;
      const currentUserId = getCurrentUserId();
      if (notification.recipientId && notification.recipientId !== currentUserId) {
        void claimIosNotification(
          notification.message.type,
          notification.message.data?.notificationId
        ).then((claim) => claim.finish?.(true));
        return;
      }
      invalidateByType(
        notification.message.type,
        notification.message.data,
        notification.message.actor?.userId
      );
      void invalidateFor(queryClient, LOCAL_EVENTS.notificationCreated);
      void showNotificationToast(notification.message, notification);
    });

    function scheduleReconnect() {
      if (isDisposed) {
        return;
      }

      clearReconnectTimer();

      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const exponentialDelay = BASE_RECONNECT_DELAY_MS * 2 ** Math.min(attempt, 5);
      const delayMs = Math.min(exponentialDelay, MAX_RECONNECT_DELAY_MS);

      reconnectTimerRef.current = window.setTimeout(() => {
        connect();
      }, delayMs);
    }

    function connect() {
      if (isDisposed) {
        return;
      }

      closeSocket();
      clearPingTimer();

      const socket = new WebSocket(activeWsUrl);
      socketRef.current = socket;
      wsInstance = socket;

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        clearReconnectTimer();
        clearPingTimer();
        // Сокет открылся - сеть есть, даже если браузер так и не прислал `online`.
        markNetworkProven();

        /*
          Пока сокета не было, события прошли мимо, и экран их не дождётся: переподключение
          перечитывает всё, что сейчас на экране. Первое открытие не в счёт - экраны только что
          загрузились сами. Если сокет вернулся вместе с сетью, проход уже сделан, и `catchUp`
          второй не запустит; остаётся случай, когда сеть была, а сокет всё равно рвался -
          перезапуск сервера сокетов, таймаут прокси, уснувший ноутбук. Разброс по времени -
          чтобы после такого перезапуска все не пришли за данными в одну секунду.

          🔴 До 26.09.2026 это делало только приложение (`VITE_IS_NATIVE`), и давно открытая
          вкладка браузера оставалась с данными на момент обрыва: возврат сети ловит
          `onlineManager`, возврат на экран - `installForegroundRefresh`, а у вкладки, которая
          всё это время была видимой и при живой сети, не срабатывает ни то, ни другое. Так на
          смоуке потерялся вход по пригласительной ссылке: хозяин остался с «нужен ещё 1 игрок»,
          хотя человек уже вошёл.
        */
        if (openedBefore) {
          catchUp(queryClient, 'socket', {
            delayMs: Math.floor(Math.random() * RECONNECT_REFRESH_JITTER_MS),
            isCancelled: () => isDisposed,
          });
        }
        openedBefore = true;

        // Always fire callbacks — on first connect and reconnect alike.
        // Child components register subscriptions via onWsReconnect before
        // this effect creates the socket (React fires child effects first),
        // so the initial subscribe message from sendWsMessage is silently
        // dropped while wsInstance is still null. Firing callbacks here
        // ensures subscriptions are sent as soon as the socket is open.
        reconnectCallbacks.forEach((cb) => cb());

        pingTimerRef.current = window.setInterval(() => {
          const current = socketRef.current;
          if (!current || current.readyState !== WebSocket.OPEN) {
            return;
          }

          try {
            current.send('ping');
          } catch {
            current.close(WS_CLOSE_CODE_PING_FAILED, 'ping_failed');
          }
        }, PING_INTERVAL_MS);
      };

      socket.onmessage = (event) => {
        if (typeof event.data !== 'string') {
          return;
        }

        handleIncomingMessage(event.data);
      };

      socket.onclose = () => {
        clearPingTimer();
        scheduleReconnect();
      };

      socket.onerror = () => {
        socket.close(WS_CLOSE_CODE_SOCKET_ERROR, 'socket_error');
      };
    }

    connect();

    return () => {
      isDisposed = true;
      for (const cancel of pendingPresentations) cancel();
      stopForegroundNotifications();
      stopAppState();
      useChatTypingStore.getState().clearAllTyping();
      clearReconnectTimer();
      clearPingTimer();
      closeSocket();
    };
  }, [isAuth, queryClient, wsUrl]);
};
